import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getAuthUser, getFileUrl } from "../../../utils/GetAuthUser";
import { Header } from "../../../components/header/Header";
import { Spinner } from "../../../components/spinner/Spinner";
import { getAllArtistRewardsThunk as  onGetAllArtistRewardsThunk} from "../../../slices/rewards/thunk";

const MyRewards = () => {

  const dispatch = useDispatch();
  const fileUrl = getFileUrl();
  const authUser=getAuthUser();

  // Selector
  const selectRewardsState = (state) => state.rewards;
  const rewardsSelector = createSelector(selectRewardsState, (reward) => ({
    isLoading: reward.isLoading,
    rewardsList: reward.rewardsList || [],
  }));

  const { rewardsList, isLoading } = useSelector(rewardsSelector);

  // Fetch API data
  useEffect(() => {
    // Fetch artist rewards (backend uses auth context)
    dispatch(onGetAllArtistRewardsThunk({artistId:authUser.user.id}));
  }, [dispatch]);

  return (
    <>
      <header className="fixed top-0 left-0 md:left-[205px] md:w-[calc(100%-205px)] z-40 bg-white w-full h-14 md:h-[84px] flex items-center justify-center px-4 sm:px-[51px] border-b border-border_color">
        <Header title="My Rewards" showBack={true} />
      </header>

      <div className="w-full xl:max-[70%] flex flex-col border border-border_color rounded-[14px] p-5 mt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-lg sm:text-[22px] text-black font-bold font-grotesk">
            My Rewards
          </span>

   
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(70vh - 100px)" }}>
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-border_color">
                  <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                    Contest
                  </th>
                  <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                    Reward Type
                  </th>
                  <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                    Reward
                  </th>
                  <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                    QR Code
                  </th>
                </tr>
              </thead>

              <tbody>
                {Array.from({ length: 6 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className={(index + 1) % 2 === 0 ? "bg-[#2878870F]" : "bg-transparent"}>
                    {/* Contest image + description skeleton */}
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gray-200 animate-pulse" />
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                      </div>
                    </td>

                    {/* Reward Type skeleton */}
                    <td className="px-3 py-3">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </td>

                    {/* Reward value skeleton */}
                    <td className="px-3 py-3">
                      <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                    </td>

                    {/* QR code skeleton */}
                    <td className="px-3 py-3">
                      <div className="w-16 h-16 bg-gray-200 rounded animate-pulse" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(70vh - 100px)" }}>
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-border_color">
                  <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                    Contest
                  </th>
                  <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                    Reward Type
                  </th>
                  <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                    Reward
                  </th>
                  <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">
                    QR Code
                  </th>
                </tr>
              </thead>

              <tbody>
                {rewardsList && rewardsList.length > 0 ? (
                  rewardsList.map((reward, index) => {
                    const contestImg = reward.contestImage ? `${fileUrl}/${reward.contestImage}` : null;
                    const qrImg = reward.gift_card_qr_code ? `${fileUrl}/${reward.gift_card_qr_code}` : null;
                    const rewardValue = reward.reward_type === "amount" ? reward.amount : reward.gift_card_number;
                    return (
                      <tr
                        key={reward.id || index}
                        className={(index + 1) % 2 === 0 ? "bg-[#2878870F]" : "bg-transparent"}
                      >
                        {/* Contest image + description */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200">
                              {contestImg ? (
                                <img
                                  src={contestImg}
                                  alt="Contest"
                                  className="w-full h-full object-cover"
                                  onError={(e) => (e.currentTarget.style.display = "none")}
                                />
                              ) : null}
                            </div>
                            <span className="text-sm tracking-wider font-medium font-grotesk text-black line-clamp-2">
                              {reward.contestDescription || ""}
                            </span>
                          </div>
                        </td>

                        {/* Reward Type */}
                        <td className="px-3 py-3 text-sm tracking-wider font-medium font-grotesk text-black capitalize">
                          {reward.reward_type || ""}
                        </td>

                        {/* Reward value */}
                        <td className="px-3 py-3 text-sm tracking-wider font-medium font-grotesk text-black">
                          {rewardValue || ""}
                        </td>

                        {/* QR code image */}
                        <td className="px-3 py-3">
                          {qrImg ? (
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200">
                              <img
                                src={qrImg}
                                alt="Gift Card QR"
                                className="w-full h-full object-cover"
                                onError={(e) => (e.currentTarget.style.display = "none")}
                              />
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="py-6 text-center text-xl tracking-wider font-bold font-grotesk text-gray-500">
                      No rewards found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default MyRewards;
